/**
 * UI-3: Request Investigation View
 *
 * Operational investigation — NOT a request detail/CRUD screen.
 * 4 zones: Situation Summary, What Led Here, Impact Signals, Recommended Actions.
 */

import { useParams, useNavigate, Link } from 'react-router-dom';
import { isRequestResolved } from '@/api';
import { useInvestigation } from '../hooks/useInvestigation';
import { useActionDispatch } from '../hooks/useActionDispatch';
import { EntityViewSkeleton } from '../components/entityViews/EntityViewSkeleton';
import { SituationBanner } from '../components/entityViews/SituationBanner';
import { RecentActivity } from '../components/entityViews/RecentActivity';
import { ImpactSignals } from '../components/entityViews/ImpactSignals';
import { RecommendedActions } from '../components/entityViews/RecommendedActions';
import { ActionToast } from '../components/entityViews/ActionToast';
import { StaffPicker } from '../components/requests/StaffPicker';

export function RequestViewPage() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();

  const { investigation, requestState, loading, error, refresh } = useInvestigation({
    entityType: 'request',
    entityId: requestId ?? '',
  });

  const { dispatch, feedback, dismissFeedback, executing } = useActionDispatch({
    entityType: 'request',
    entityId: requestId ?? '',
    onRefresh: refresh,
  });

  if (!requestId) {
    return (
      <div className="flex items-center justify-center h-full text-slate-500">
        No request ID specified
      </div>
    );
  }

  if (loading) return <EntityViewSkeleton />;

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="text-rose-400 text-sm">{error}</div>
        <button type="button" onClick={refresh} className="px-4 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors">
          Retry
        </button>
      </div>
    );
  }

  if (!investigation) return null;

  // SLA timer (only for active requests)
  const slaInfo = requestState?.slaDeadline
    ? (() => {
        const deadline = new Date(requestState.slaDeadline).getTime();
        const remaining = deadline - Date.now();
        // OPS-38.5.5: COMPLETED is the canonical "resolved" axis
        // (`isRequestResolved`). CANCELLED is not a `GuestRequestStatus`
        // enum member — it is set by the cancel route on `RequestState` —
        // so the atom check stays inline as boundary translation per §4.
        const isResolved = isRequestResolved(requestState.status) || requestState.status === 'CANCELLED';
        if (isResolved) return null;

        if (remaining < 0) {
          const overdue = Math.abs(remaining);
          const hours = Math.floor(overdue / (1000 * 60 * 60));
          const minutes = Math.floor((overdue % (1000 * 60 * 60)) / (1000 * 60));
          return { label: `${hours}h ${minutes}m overdue`, critical: true };
        }
        const hours = Math.floor(remaining / (1000 * 60 * 60));
        const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
        return { label: `${hours}h ${minutes}m remaining`, critical: hours < 1 };
      })()
    : null;

  return (
    <div className="h-full flex flex-col bg-slate-900">
      {/* ── NAV BAR ────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-20 bg-slate-800/95 backdrop-blur-sm border-b border-slate-700 px-6 py-3">
        <div className="flex items-center gap-3">
          <Link to="/requests" className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors text-slate-400 hover:text-white" title="Back to Requests" aria-label="Back to Requests">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <span className="text-sm font-medium text-white">Request {requestId.slice(0, 8)}…</span>
          <span className="text-xs text-slate-500">Investigation</span>

          {/* SLA badge in header */}
          {slaInfo && (
            <span className={`text-[10px] px-2 py-1 rounded-full border ml-2 ${
              slaInfo.critical
                ? 'bg-rose-900/30 text-rose-300 border-rose-700 animate-pulse'
                : 'bg-slate-700/40 text-slate-300 border-slate-600'
            }`}>
              SLA: {slaInfo.label}
            </span>
          )}

          <button type="button" onClick={refresh} className="ml-auto p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors" title="Refresh">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </header>

      {/* ── 4 INVESTIGATION ZONES ──────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 max-w-3xl mx-auto w-full">
        <SituationBanner situation={investigation.situation} />

        {/* ── STAFF ALLOCATION (PILOT-26) ───────────────────────────────── */}
        {requestState && !isRequestResolved(requestState.status) && requestState.status !== 'CANCELLED' && (
          <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Staff Assignment</h3>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mb-4">
              {requestState.roomNumber && (
                <>
                  <span className="text-slate-500">Room</span>
                  <span className="text-white font-medium">{requestState.roomNumber}</span>
                </>
              )}
              {requestState.guestName && (
                <>
                  <span className="text-slate-500">Guest</span>
                  <span className="text-white">{requestState.guestName}</span>
                </>
              )}
              {requestState.category && (
                <>
                  <span className="text-slate-500">Category</span>
                  <span className="text-white">{requestState.category}</span>
                </>
              )}
              <span className="text-slate-500">Status</span>
              <span className={`font-medium ${
                requestState.status === 'ASSIGNED' ? 'text-indigo-300' :
                requestState.status === 'IN_PROGRESS' ? 'text-amber-300' :
                requestState.status === 'ESCALATED' ? 'text-rose-300' :
                'text-slate-300'
              }`}>{requestState.status}</span>
            </div>
            <StaffPicker
              requestId={requestId}
              currentAssigneeName={requestState.assignedTo}
              onAssigned={refresh}
              disabled={executing}
            />
          </div>
        )}

        <RecentActivity events={investigation.recentEvents} anomalies={investigation.anomalies} />
        <ImpactSignals
          signals={investigation.impactSignals}
          onNavigate={(entityType, entityId) => {
            if (entityType === 'room') navigate(`/rooms/${entityId}`);
            else if (entityType === 'guest') navigate(`/guests/${entityId}`);
          }}
        />
        <RecommendedActions
          actions={investigation.actions}
          disabled={executing}
          onAction={(actionType) => {
            if (actionType === 'inspect-room' && requestState?.roomNumber) {
              navigate(`/rooms/${requestState.roomNumber}`); return;
            }
            dispatch(actionType);
          }}
        />
      </div>

      <ActionToast feedback={feedback} onDismiss={dismissFeedback} />
    </div>
  );
}
